import React, { useState, useEffect, useRef } from 'react';

export interface FishingMinigameState {
  isActive: boolean;
  bobberY: number;
  bobberVelocity: number;
  catchZoneY: number;
  catchZoneSize: number;
  catchZoneVelocity: number;
  progressMeter: number;
  difficultyLevel: number;
  isSpacePressed: boolean;
  spotType: string;
  spotId: string;
}

export interface FishingMinigameProps {
  isActive: boolean;
  spotType?: string;
  spotId?: string;
  onCatchComplete: (quality: number) => void;
  onDeactivate: () => void;
}

const BAR_WIDTH = 40;
const BAR_HEIGHT = 200;
const BOBBER_SIZE = 16;
const CATCH_ZONE_MIN_SIZE = 0.15;
const CATCH_ZONE_MAX_SIZE = 0.35;

const GRAVITY = 0.0025;
const LIFT_FORCE = 0.008;
const MAX_VELOCITY = 0.018;
const BOUNCE_DAMPING = 0.4;
const VELOCITY_SMOOTHING = 0.15;

const PROGRESS_GAIN_RATE = 1.4;
const PROGRESS_DECAY_RATE = 0.6;
const CATCH_THRESHOLD = 100;

const ZONE_SPEED_MIN = 0.0012;
const ZONE_SPEED_MAX = 0.004;
const ZONE_DIRECTION_CHANGE_CHANCE = 0.008;
const ZONE_ACCELERATION = 0.00015;

const SPOT_DIFFICULTY: Record<string, number> = {
  shallow_waters: 1,
  deep_sea: 2,
  coral_reef: 3,
  fishing_hole: 4,
};

export const FishingMinigame: React.FC<FishingMinigameProps> = ({
  isActive,
  spotType = 'shallow_waters',
  spotId = '',
  onCatchComplete,
  onDeactivate,
}) => {
  const [state, setState] = useState<FishingMinigameState>({
    isActive: false,
    bobberY: 0.5,
    bobberVelocity: 0,
    catchZoneY: 0.5,
    catchZoneSize: 0.25,
    catchZoneVelocity: ZONE_SPEED_MIN,
    progressMeter: 25,
    difficultyLevel: 1,
    isSpacePressed: false,
    spotType: 'shallow_waters',
    spotId: '',
  });

  const keysRef = useRef<{ [key: string]: boolean }>({});
  const animationFrameRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(Date.now());

  const panelWidth = 100;
  const panelHeight = BAR_HEIGHT + 100;

  useEffect(() => {
    if (!isActive) return;

    const difficulty = SPOT_DIFFICULTY[spotType] || 1;
    const zoneSize = CATCH_ZONE_MAX_SIZE - (difficulty - 1) * 0.05;
    const zoneSpeed = ZONE_SPEED_MIN + (difficulty - 1) * 0.001;

    setState({
      isActive: true,
      bobberY: 0.5,
      bobberVelocity: 0,
      catchZoneY: 0.3 + Math.random() * 0.4,
      catchZoneSize: Math.max(CATCH_ZONE_MIN_SIZE, zoneSize),
      catchZoneVelocity: zoneSpeed * (Math.random() > 0.5 ? 1 : -1),
      progressMeter: 25,
      difficultyLevel: difficulty,
      isSpacePressed: false,
      spotType,
      spotId,
    });

    lastTimeRef.current = Date.now();
  }, [isActive, spotType, spotId]);

  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Block all keybinds during fishing minigame
      if (e.code === 'Space') {
        e.preventDefault();
        e.stopPropagation();
        keysRef.current['SPACE'] = true;
        console.log('🎣 FishingMinigame: SPACE key pressed');
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onDeactivate();
      } else {
        // Block all other keys to prevent UI panel triggers
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      // Block all keybinds during fishing minigame
      if (e.code === 'Space') {
        e.preventDefault();
        e.stopPropagation();
        keysRef.current['SPACE'] = false;
        console.log('🎣 FishingMinigame: SPACE key released');
      } else {
        // Block all other keys to prevent UI panel triggers
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Use capture phase to ensure we get events before Phaser
    // Only add listeners when minigame is active
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('keyup', handleKeyUp, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('keyup', handleKeyUp, true);
    };
  }, [isActive, onDeactivate]);

  useEffect(() => {
    if (!isActive) return;

    console.log('🎣 FishingMinigame: Starting game loop');
    const update = () => {
      setState((prevState) => {
        let newState = { ...prevState };

        const isSpacePressed = keysRef.current['SPACE'] || false;
        newState.isSpacePressed = isSpacePressed;

        // Debug logging for SPACE key state
        if (isSpacePressed && !prevState.isSpacePressed) {
          console.log('🎣 FishingMinigame: SPACE key is DOWN in game loop');
        } else if (!isSpacePressed && prevState.isSpacePressed) {
          console.log('🎣 FishingMinigame: SPACE key is UP in game loop');
        }

        let targetVelocity = newState.bobberVelocity;
        if (isSpacePressed) {
          targetVelocity -= LIFT_FORCE;
        } else {
          targetVelocity += GRAVITY;
        }

        newState.bobberVelocity += (targetVelocity - newState.bobberVelocity) * VELOCITY_SMOOTHING;
        newState.bobberVelocity = Math.max(-MAX_VELOCITY, Math.min(MAX_VELOCITY, newState.bobberVelocity));

        newState.bobberY += newState.bobberVelocity;

        if (newState.bobberY <= 0) {
          newState.bobberY = 0;
          newState.bobberVelocity = Math.max(0, newState.bobberVelocity * BOUNCE_DAMPING);
        } else if (newState.bobberY >= 1) {
          newState.bobberY = 1;
          newState.bobberVelocity = Math.min(0, newState.bobberVelocity * BOUNCE_DAMPING);
        }

        newState.catchZoneY += newState.catchZoneVelocity;

        const halfZone = newState.catchZoneSize / 2;
        const minY = halfZone;
        const maxY = 1 - halfZone;

        if (newState.catchZoneY <= minY) {
          newState.catchZoneY = minY;
          if (newState.catchZoneVelocity < 0) {
            newState.catchZoneVelocity = Math.abs(newState.catchZoneVelocity);
          }
        } else if (newState.catchZoneY >= maxY) {
          newState.catchZoneY = maxY;
          if (newState.catchZoneVelocity > 0) {
            newState.catchZoneVelocity = -Math.abs(newState.catchZoneVelocity);
          }
        }

        if (Math.random() < ZONE_DIRECTION_CHANGE_CHANCE) {
          newState.catchZoneVelocity *= -1;
        }

        if (Math.random() < 0.005) {
          const baseSpeed = ZONE_SPEED_MIN + (newState.difficultyLevel - 1) * 0.0008;
          const currentSpeed = Math.abs(newState.catchZoneVelocity);
          const targetSpeed = baseSpeed + (Math.random() - 0.5) * 0.0015;
          const newSpeed = currentSpeed + (targetSpeed - currentSpeed) * ZONE_ACCELERATION;
          const clampedSpeed = Math.max(ZONE_SPEED_MIN, Math.min(ZONE_SPEED_MAX, newSpeed));
          newState.catchZoneVelocity = clampedSpeed * Math.sign(newState.catchZoneVelocity);
        }

        const zoneTop = newState.catchZoneY - halfZone;
        const zoneBottom = newState.catchZoneY + halfZone;
        const isInZone = newState.bobberY >= zoneTop && newState.bobberY <= zoneBottom;

        if (isInZone) {
          const distFromCenter = Math.abs(newState.bobberY - newState.catchZoneY);
          const closeness = 1 - (distFromCenter / halfZone);
          const bonus = closeness > 0.8 ? 1.3 : 1.0;
          newState.progressMeter = Math.min(CATCH_THRESHOLD, newState.progressMeter + PROGRESS_GAIN_RATE * bonus * (1 + closeness * 0.3));
        } else {
          newState.progressMeter = Math.max(0, newState.progressMeter - PROGRESS_DECAY_RATE);
        }

        if (newState.progressMeter >= CATCH_THRESHOLD) {
          const quality = Math.min(100, newState.progressMeter);
          console.log(`🎣 FishingMinigame: Catch complete! Quality: ${quality}%`);
          onCatchComplete(quality);
          onDeactivate();
          newState.isActive = false;
          return newState;
        }

        return newState;
      });

      animationFrameRef.current = requestAnimationFrame(update);
    };

    animationFrameRef.current = requestAnimationFrame(update);

    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isActive, onCatchComplete, onDeactivate]);

  if (!isActive) return null;

  const halfZone = state.catchZoneSize / 2;
  const zoneTop = state.catchZoneY - halfZone;
  const zoneBottom = state.catchZoneY + halfZone;
  const isInZone = state.bobberY >= zoneTop && state.bobberY <= zoneBottom;

  const bobberY = (state.bobberY - 0.5) * BAR_HEIGHT;
  const zoneHeight = state.catchZoneSize * BAR_HEIGHT;
  const zoneY = (state.catchZoneY - 0.5) * BAR_HEIGHT;
  const fillWidth = (state.progressMeter / CATCH_THRESHOLD) * BAR_WIDTH;

  let progressColor = '#fc8181';
  if (state.progressMeter >= 80) {
    progressColor = '#22c55e';
  } else if (state.progressMeter >= 50) {
    progressColor = '#f6e05e';
  } else if (state.progressMeter >= 25) {
    progressColor = '#5dade2';
  }

  let qualityColor = '#fc8181';
  if (state.progressMeter >= 80) {
    qualityColor = '#22c55e';
  } else if (state.progressMeter >= 50) {
    qualityColor = '#f6e05e';
  } else if (state.progressMeter >= 25) {
    qualityColor = '#5dade2';
  }

  return (
    <div
      style={{
        position: 'fixed',
        right: 30,
        top: '50%',
        transform: 'translateY(-50%)',
        width: panelWidth,
        height: panelHeight,
        background: '#0a0a0a',
        border: '2px solid #3a3a3a',
        zIndex: 900,
        fontFamily: '"Sometype Mono", monospace',
        color: '#e2e8f0',
        display: 'flex',
        flexDirection: 'column',
        padding: '8px',
        boxSizing: 'border-box',
      }}
    >
      {/* Title */}
      <div
        style={{
          textAlign: 'center',
          fontSize: '12px',
          fontWeight: 'bold',
          color: '#5dade2',
          marginBottom: '8px',
          paddingBottom: '6px',
          borderBottom: '1px solid #1a1a2e',
        }}
      >
        FISHING
      </div>

      {/* Bar area */}
      <div
        style={{
          position: 'relative',
          width: BAR_WIDTH,
          height: BAR_HEIGHT,
          margin: '0 auto 8px',
          background: '#0d1117',
          border: '2px solid #30363d',
          boxSizing: 'border-box',
        }}
      >
        {/* Catch zone */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: `${50 + (zoneY / BAR_HEIGHT) * 50}%`,
            transform: 'translate(-50%, -50%)',
            width: BAR_WIDTH - 4,
            height: zoneHeight,
            background: '#22c55e',
            border: '2px solid #22c55e',
            opacity: 0.4,
          }}
        />

        {/* Bobber */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: `${50 + (bobberY / BAR_HEIGHT) * 50}%`,
            transform: 'translate(-50%, -50%)',
            width: BOBBER_SIZE,
            height: BOBBER_SIZE,
            background: isInZone ? '#22c55e' : '#5dade2',
            border: `2px solid ${isInZone ? '#22c55e' : '#5dade2'}`,
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Progress bar */}
      <div
        style={{
          width: BAR_WIDTH,
          height: 10,
          margin: '0 auto 6px',
          background: '#21262d',
          border: '1px solid #30363d',
          position: 'relative',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${(fillWidth / BAR_WIDTH) * 100}%`,
            background: progressColor,
            transition: 'width 50ms linear',
          }}
        />
      </div>

      {/* Quality text */}
      <div
        style={{
          textAlign: 'center',
          fontSize: '10px',
          fontWeight: 'bold',
          color: qualityColor,
          marginBottom: '4px',
        }}
      >
        CATCH: {Math.round(state.progressMeter)}%
      </div>

      {/* Instructions */}
      <div
        style={{
          textAlign: 'center',
          fontSize: '9px',
          color: state.isSpacePressed ? '#22c55e' : '#8b949e',
          fontWeight: state.isSpacePressed ? 'bold' : 'normal',
        }}
      >
        [{state.isSpacePressed ? 'SPACE ✓' : 'SPACE'}]
      </div>
    </div>
  );
};
